import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Dropdown, LoadingSpinner, Checkbox } from 'yankees-react-utils';
import { useTranslation } from 'react-i18next';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Ballcap, useOktaQuery } from '../../data';
import BallcapSession from './BallcapSession';
import usePlayerSelector from '../../utils/usePlayerSelector';

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 10px;
`;
const HeaderContent = styled.div`
    display: flex;
    align-items: center;
    flex: 1;
    h2 {
        font-weight: bold;
        margin-right: 25px;
        margin-bottom: 0;
    }
    label {
        font-size: 14px;
        margin-bottom: 0;
        white-space: nowrap;
    }
    .nyy-checkbox-radio-container label {
        color: inherit;
        line-height: 20px;
    }
    .nyy-checkbox-radio-container label:before {
        top: 0;
    }
`;

const TRACKMAN_SESSION_SOURCE = 1;

const isTrackmanSession = (session) => session?.SessionSource === TRACKMAN_SESSION_SOURCE;

const getSessionIdentity = (session) => (isTrackmanSession(session)
    ? `trackman:${session.SessionGuid}`
    : `ballcap:${session.SessionID}`);

const getSessionYears = (sessions) => [...new Set(sessions
    .map((session) => session.Season)
    .filter((season) => Number.isFinite(season)))]
    .sort((a, b) => b - a);

const getSessionPath = (prefix, session) => (isTrackmanSession(session)
    ? `${prefix}trackman/${session.SessionGuid}`
    : `${prefix}${session.SessionID}`);

const getSessionDetail = (accessToken, playerid, session) => (isTrackmanSession(session)
    ? Ballcap.getTrackmanPen(accessToken, playerid, session.SessionGuid)
    : Ballcap.getSessionV2(accessToken, playerid, session.SessionID));

const formatTime = (createdDate) => {
    if (!createdDate) return '';
    const d = new Date(createdDate);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const getSessionLabel = (session, translate, includeTime = false) => {
    const date = session.CreatedDate
        ? translate('format.datetime', { val: new Date(session.CreatedDate), interpolation: { escapeValue: false } })
        : translate('common.unknown');
    const time = includeTime ? formatTime(session.CreatedDate) : '';
    const timePart = time ? ` - ${time}` : '';
    let description = session.LocationName ? ` (${session.LocationName})` : '';
    if (isTrackmanSession(session)) {
        description = ` (Trackman: ${session.SessionTypeName || translate('common.unknown')})`;
    }
    return `${date}${timePart}${description}`;
};

const getYearOptions = (years) => years.map((year) => ({
    name: year.toString(),
    value: year,
}));

const getSessionOptions = (sessions, translate) => {
    const baseLabels = sessions.map((session) => getSessionLabel(session, translate));
    const baseLabelCounts = baseLabels.reduce((acc, label) => {
        acc[label] = (acc[label] || 0) + 1;
        return acc;
    }, {});

    const options = sessions.map((session, index) => {
        const isDuplicate = baseLabelCounts[baseLabels[index]] > 1;
        return {
            name: isDuplicate ? getSessionLabel(session, translate, true) : baseLabels[index],
            value: getSessionIdentity(session),
        };
    });

    const seen = {};
    return options.map((option) => {
        seen[option.name] = (seen[option.name] || 0) + 1;
        return seen[option.name] > 1 ? { ...option, name: `${option.name} (${seen[option.name]})` } : option;
    });
};

const BallcapSessionContainer = ({ match: { url, path } }) => {
    const [selectedSessionData, setSelectedSessionData] = useState(null);
    const [showAllCheckbox, setShowAllCheckbox] = useState(false);

    const { t: translate } = useTranslation();
    const navigate = useHistory();
    const { PlayerID } = usePlayerSelector();
    const prefix = `${url}/`;

    const trackmanSessionMatch = useRouteMatch(`${path}/trackman/:sessionGuid`);
    const ballcapSessionMatch = useRouteMatch(`${path}/:sessionId(\\d+)`);
    let urlSessionIdentity = null;
    if (trackmanSessionMatch) {
        urlSessionIdentity = `trackman:${trackmanSessionMatch.params.sessionGuid}`;
    } else if (ballcapSessionMatch) {
        urlSessionIdentity = `ballcap:${parseInt(ballcapSessionMatch.params.sessionId)}`;
    }

    const { data: allSessions = [], isFetching: isFetchingSessions } = useOktaQuery(
        ['ballcap', PlayerID],
        (accessToken) => Ballcap.getPlayerSessions(accessToken, PlayerID),
    );
    const selectedSession = useMemo(() => (
        urlSessionIdentity
            ? allSessions.find((session) => getSessionIdentity(session) === urlSessionIdentity) || null
            : null
    ), [urlSessionIdentity, allSessions]);

    const { isFetching: isFetchingSession } = useOktaQuery(
        ['ballcap-session', PlayerID, urlSessionIdentity],
        (accessToken) => getSessionDetail(accessToken, PlayerID, selectedSession),
        {
            enabled: !!selectedSession,
            onSuccess: (data) => setSelectedSessionData(data),
        },
    );

    const yearOptions = useMemo(
        () => getYearOptions(getSessionYears(allSessions)),
        [allSessions],
    );
    const selectedYearDropdown = useMemo(() => {
        const selectedSeason = selectedSession?.Season ?? allSessions[0]?.Season;
        return yearOptions.find((year) => year.value === selectedSeason) || null;
    }, [allSessions, selectedSession, yearOptions]);

    const sessionsForYear = useMemo(() => {
        if (!allSessions.length || !selectedYearDropdown?.value) {
            return [];
        }
        return allSessions.filter((session) => session.Season === selectedYearDropdown.value);
    }, [allSessions, selectedYearDropdown]);

    const sessionOptions = useMemo(
        () => getSessionOptions(sessionsForYear, translate),
        [sessionsForYear, translate],
    );

    const selectedSessionDropdown = useMemo(() => (
        urlSessionIdentity && sessionOptions.length
            ? sessionOptions.find((option) => option.value === urlSessionIdentity) || null
            : null
    ), [urlSessionIdentity, sessionOptions]);

    useEffect(() => {
        if (!allSessions.length || urlSessionIdentity) return;
        navigate.replace(getSessionPath(prefix, allSessions[0]));
    }, [allSessions, urlSessionIdentity, navigate, prefix]);

    useEffect(() => {
        if (!sessionOptions.length) return;
        const isUrlValid = urlSessionIdentity
            && sessionOptions.some((option) => option.value === urlSessionIdentity);
        if (!isUrlValid) {
            const target = allSessions.find(
                (session) => getSessionIdentity(session) === sessionOptions[0].value,
            );
            if (target) navigate.replace(getSessionPath(prefix, target));
        }
    }, [sessionOptions, urlSessionIdentity, prefix, navigate, allSessions]);

    const handleSessionSelect = (option) => {
        if (!option?.value || option.value === urlSessionIdentity) return;
        const target = allSessions.find((session) => getSessionIdentity(session) === option.value);
        if (target) navigate.push(getSessionPath(prefix, target));
    };

    const handleYearSelect = (option) => {
        if (!option?.value || option.value === selectedYearDropdown?.value) return;
        const target = allSessions.find((session) => session.Season === option.value);
        if (target) navigate.push(getSessionPath(prefix, target));
    };

    const isWaitingForSessions = isFetchingSessions && !allSessions.length;

    return (
        <div className="text-center">
            <Header>
                <HeaderContent>
                    <h2>{selectedSession?.SessionTypeName === 'Bullpen' ? translate('home.sessions-bullpen') : translate('home.sessions-cage') || '--'}</h2>
                    {isWaitingForSessions && (<LoadingSpinner />)}
                    {!isWaitingForSessions && (
                        <>
                            <span style={{ marginLeft: 'auto' }}>
                                <Checkbox
                                    name="ballcap-showall"
                                    id="ballcap-showall"
                                    label={`Show all ${selectedSession?.SessionType === 1 ? 'pitches' : 'swings'}:`}
                                    labelDirection="left"
                                    checked={showAllCheckbox}
                                    onChange={() => setShowAllCheckbox(!showAllCheckbox)}
                                    style={{ color: 'unset' }}
                                />
                            </span>
                            <label style={{ marginLeft: '10px', marginRight: '5px' }}>{translate('common.year')}:</label>
                            <Dropdown
                                name="ballcap-year"
                                title="Year"
                                value={selectedYearDropdown}
                                onSelect={handleYearSelect}
                                options={yearOptions}
                                disabled={!yearOptions.length}
                            />
                            <label style={{ marginLeft: '10px', marginRight: '5px' }}>{translate('components.ballcap.session')}:</label>
                            <Dropdown
                                name="ballcap-session"
                                title="Session"
                                value={selectedSessionDropdown}
                                onSelect={handleSessionSelect}
                                options={sessionOptions}
                                disabled={!sessionOptions.length}
                            />
                            {isFetchingSession && selectedSessionData != null && (
                                <span style={{ marginLeft: '10px', display: 'inline-flex', alignItems: 'center' }}>
                                    <LoadingSpinner />
                                </span>
                            )}
                        </>
                    )}
                </HeaderContent>
            </Header>
            {isWaitingForSessions && (<LoadingSpinner />)}
            {!isWaitingForSessions && sessionsForYear.length === 0 && sessionOptions.length === 0 && yearOptions.length === 0 && (
                <h3 className="d-flex justify-content-center py-5">
                    {translate('sections.ballcap.no-sessions-found')}
                </h3>
            )}
            {!isWaitingForSessions && sessionsForYear.length > 0 && sessionOptions.length > 0 && yearOptions.length > 0 && (
                <BallcapSession
                    isLoading={isFetchingSession}
                    isBullpen={selectedSession?.SessionType === 1}
                    isTrackmanSyntheticSession={selectedSession?.SessionSource === 1}
                    sessionData={selectedSessionData}
                    showAllCheckbox={showAllCheckbox}
                />
            )}
        </div>
    );
};

BallcapSessionContainer.propTypes = {
    match: PropTypes.shape({
        path: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
    }).isRequired,
};

export default BallcapSessionContainer;
